import React, { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { IndependentAlignmentVM } from './IndependentAlignmentVM';
import { mdiPencil, mdiPlusBoxOutline, mdiTrashCanOutline } from '@mdi/js';
import arrowCursorMove from 'cd-eft/assets/images/arrow-cursor-move.svg';
import updateCurves from 'cd-eft/assets/images/update-curves.svg';
import applyDefaultCurves from 'cd-eft/assets/images/apply-default-curves.svg';
import stores from 'cd-eft/models/Stores';
import Icon from '@mdi/react';
import { DeleteElement } from 'cd-eft/components/Design/common/DeleteElement';
import { Accordion } from 'cd-eft/components/common/Accordion';
import './IndependentAlignment.scss';
import { DynamicAlignmentProfile } from 'cd-eft/components/common/DynamicProfile/DynamicAlignmentProfile';
import { DynamicAlignmentHub } from 'cd-eft/models/Stores/DynamicProfile/Alignment/DynamicAlignmentHub';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import { DynamicTableStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicTableStoreBase';
import { ProfileStoreType } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileEnums';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { DynamicAlignmentMapStore } from 'cd-eft/models/Stores/DynamicProfile/Alignment/DynamicAlignmentMapStore';
import { AlignmentTable } from '../AlignmentTable/AlignmentTable';

const { alignmentDesignStore: alignmentStore, projectsLibraryStore: libraryStore, designStore } = stores.AppStore;

const IndependentAlignmentComponent = (props) => {
  const history = useHistory();
  let profileHub: DynamicAlignmentHub;

  const profileStoreRef = useRef<DynamicProfileStoreBase>();
  const profileTableStoreRef = useRef<DynamicTableStoreBase>();

  if (!profileTableStoreRef.current && !profileStoreRef.current) {
    profileHub = new DynamicAlignmentHub();
    profileHub.createStore(ProfileStoreType.MAP, { mapView: EngineeringMapService.instance?.mapView });
    profileHub.createStore(ProfileStoreType.PROFILE);
    profileStoreRef.current = profileHub.ProfileStore;
    profileHub.createStore(ProfileStoreType.TABLE);
    profileTableStoreRef.current = profileHub.TableStore;
  }

  useEffect(() => {
    if (!designStore.hasProject()) {
      vm.noProjecMessage(history);
    }
    return () => {
      profileHub?.Dispose();
    };
  }, []);

  const vmRef = useRef<IndependentAlignmentVM | null> (null);

  if (!vmRef.current) {
    vmRef.current = new IndependentAlignmentVM({ ...props, profileTableStore: profileTableStoreRef.current });
  }
  const vm = vmRef.current;
  const currentObject = alignmentStore.alignmentObject;

  useEffect(() => {
    vm.onMount();

    function cleanup() {
      vm.onUnmount();
    }
    return cleanup;
  }, []);

  return (
    <div data-testid="alignment">
      <form className="usa-form usa-form--large form-with-fieldset" name="alignmentForm">
        <div className="usa-fieldset p-2 mt-4">
          <div className="form-row-fixed-height">
            <div className="grid-col-3"></div>
            <div className="usa-radio">
              <input
                className="usa-radio__input usa-radio__input--tile"
                id="auto-generate"
                data-testid="auto-generate"
                type="radio"
                name="auto-generate"
                value="autoGenerate"
                disabled={
                  designStore.currentSurface.surfaceDefClassName === 'dem' ||
                  vm.currentObject.editingStatus === 'update'
                }
                checked={vm.autoGenerate}
                onChange={vm.onChangeAutoGenerate}
              />
              <label className="usa-radio__label radio-custom-btn" htmlFor="auto-generate">
                Auto Generate
              </label>
            </div>

            <div className="usa-radio">
              <input
                className="usa-radio__input usa-radio__input--tile"
                id="manual-digitize"
                data-testid="manual-digitize"
                type="radio"
                name="manual-digitize"
                value="manualDigitize"
                checked={!vm.autoGenerate}
                onChange={vm.onChangeManualDigitize}
              />
              <label className="usa-radio__label radio-custom-btn" htmlFor="manual-digitize">
                Manual Digitize
              </label>
            </div>
          </div>
          <div className="form-row-fixed-height">
            <label className="grid-col-3 usa-label pr-3" htmlFor="Description">
              Generate Alignment
            </label>
            <select
              value={vm.selectedDescription}
              name="Description"
              onChange={vm.handleDescriptionChange}
              disabled={!vm.autoGenerate}
              className="grid-col-3"
            >
              <option value="Description">{'Description'}</option>
              {vm.uniqueDescriptions.length > 0 &&
                vm.uniqueDescriptions.map((value, index) => (
                  <option key={`Description-${index}`} value={value}>
                    {value}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-row-fixed-height">
            <label className="grid-col-3 usa-label pr-3" htmlFor="alignmentName">
              Alignment Name
            </label>
            <input
              className={
                vm.formValidation.formErrors.alignmentName !== '' ? 'usa-input--error' : 'grid-col-3 usa-input'
              }
              name="alignmentName"
              aria-label="alignmentName"
              data-testid="alignmentName"
              placeholder={currentObject.alignmentName}
              value={currentObject.alignmentName}
              onChange={vm.setAlignmentName}
              type="text"
            />
            <div className="ml-2 display-flex align-items-center">
              <input
                id="toggleShowTerrace"
                className="usa-checkbox__input"
                type="checkbox"
                name="toggleShowTerrace"
                disabled={vm.autoGenerate}
                checked={vm.isShowTerraceChecked}
                onChange={vm.onChangeShowTerrace}
              />
              <label className="usa-checkbox__label" htmlFor="toggleShowTerrace">
                Show Terraces
              </label>
            </div>
          </div>
          <span className="usa-error-message" id="client--message" role="alert">
            {vm.formValidation.formErrors.alignmentName}
          </span>
          <div className="form-row-fixed-height">
            <label className="grid-col-3 usa-label" htmlFor="beginning-station">
              Beginning Station
            </label>
            <input
              className="grid-col-3 usa-input"
              name="beginning-station"
              aria-label="beginning-station"
              data-testid="beginning-station"
              type="number"
              placeholder="0"
              value={vm.currentObject.alignmentStations[0]?.stationName || vm.inputValue}
              onChange={vm.setBeginningStation}
            />
            <button
              className="usa-button ml-2"
              onClick={vm.autoGenerateAlignment}
              disabled={!vm.autoGenerate || vm.selectedDescription === 'Description'}
            >
              Generate
            </button>
          </div>
        </div>

        <Accordion headers={['Profile']} expanded={true}>
          <>
            {!libraryStore.resetZValue && (
              <DynamicAlignmentProfile profileStore={profileStoreRef.current} editable={false} />
            )}
            <div className="usa-fieldset mt-4">
              <div className="display-flex">
                <button
                  className="usa-button usa-button--profile usa-button--short"
                  onClick={vm.sketchAlignment}
                  disabled={vm.autoGenerate}
                >
                  <Icon
                    path={mdiPencil}
                    title="Digitize Reaches"
                    size={1.5}
                    className="esri-collapse__icon esri-icon-public mr-2"
                  />
                  <span className="standard-text">Digitize Alignment</span>
                </button>
                <button className="usa-button usa-button--profile usa-button--short" onClick={vm.insertStation}>
                  <Icon
                    path={mdiPlusBoxOutline}
                    title="Digitize Reaches"
                    size={1.5}
                    className="esri-collapse__icon esri-icon-public mr-2"
                  />
                  <span className="standard-text">Insert Station</span>
                </button>
                <button
                  className="usa-button usa-button--profile usa-button--indv usa-button--short"
                  onClick={vm.moveStation}
                >
                  <img className="mr-2" src={arrowCursorMove} alt="" />
                  <span className="standard-text">Move Station</span>
                </button>
                <button className="usa-button usa-button--profile usa-button--short" onClick={vm.deleteStation}>
                  <Icon
                    path={mdiTrashCanOutline}
                    title="Delete Waterway Vertex"
                    size={1.5}
                    className="esri-collapse__icon esri-icon-public mr-2"
                  />
                  <span className="standard-text">Delete Station</span>
                </button>
                <button className="usa-button usa-button--profile" onClick={vm.applyDefaultCurves}>
                  <img className="mr-2" src={applyDefaultCurves} alt={'Apply Default Curves'} />
                  <span>Apply Default Curve</span>
                </button>
                {alignmentStore.alignmentObject.alignmentStations.reduce((acc, el) => acc + el.curveValue, 0) > 0 && (
                  <button className="usa-button mx-0 usa-button--profile usa-button--short" onClick={vm.updateCurves}>
                    <img className="mr-2" src={updateCurves} alt={'Update Curves'} />
                    <span>Update Curves</span>
                  </button>
                )}
              </div>
            </div>
          </>
        </Accordion>

        <Accordion headers={['Table']} expanded={true}>
          <AlignmentTable
            currentObject={currentObject}
            handleRowMouseEnter={vm.handleRowMouseEnter}
            handleRowMouseLeave={vm.handleRowMouseLeave}
            updateSingleAlignmentStation={vm.updateSingleAlignmentStation}
            updateMapAndProfile={vm.updateMapAndProfile}
            handleCurveValueInputFocus={vm.handleCurveValueInputFocus}
            vm={vm}
            enableAdvancedFeatures={true}
          />
        </Accordion>

        {/* Action Buttons */}
        <div className="text-right">
          <button className="usa-button usa-button--outline" onClick={vm.cancelEditIndependentAlignment}>
            Close
          </button>
          <button className="usa-button" disabled={!designStore.hasProject()} onClick={vm.saveIndependentAlignment}>
            Save
          </button>
          <button
            className="usa-button mr-0"
            disabled={!designStore.hasProject()}
            onClick={(e) => vm.saveIndependentAlignment(e, true)}
          >
            Save & Close
          </button>
        </div>
      </form>

      {alignmentStore.deleteAlStDialoglIsOpen && (
        <DeleteElement
          trigger={alignmentStore.deleteAlStDialoglIsOpen}
          setTrigger={alignmentStore.toggleDeleteAlStDialog}
          element="alignment station"
          name={alignmentStore.alignmentStationToDelete.stationName}
          action={vm.deleteSingleAlignmentStation}
        />
      )}
    </div>
  );
};

export const IndependentAlignment = observer(IndependentAlignmentComponent);